<template>
  <section class="py-24">
    <MicsContainer>
      <header class="mb-12" id="available-today">
        <h2 class="leading-tight text-4xl font-extrabold">{{$t('furnished_flats')}} {{$t('countries.' + $helpers.getConfigurationKey('countryCode'))}}</h2>
      </header>
      <div v-if="locations.length" class="mb-8 flex flex-wrap items-center justify-between gap-4 md:gap-0">
        <select id="default"
                class="md:hidden w-full bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                @change="setLocationIndex">
          <option v-for="(location, index) in locations" :selected="location.name == activeLocation?.name" :value="index">{{$t('cities.' + location.name)}}</option>
        </select>
        <ul class="hidden md:flex flex-wrap gap-8 text-sm font-medium text-center text-gray-500 dark:text-gray-400">
          <li v-for="location in locations">
            <a href="#"  @click.prevent="setLocation(location)" class="inline-block px-5 py-2.5 rounded-md" :class="location.name == activeLocation?.name ? 'text-white bg-primary-900' : 'hover:text-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 dark:hover:text-white'">{{$t('cities.' + location.name)}}</a>
          </li>
        </ul>

        <div class="w-full md:w-auto">
          <label for="available-today-period" class="block mb-2 text-sm font-medium text-gray-900 dark:text-white sr-only">{{$t('labels.period')}}</label>
          <select id="available-today-period"
                  v-model="selectedPeriod"
                  @change="setPeriod"
                  class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full md:w-fit p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 xl:min-w-[160px] 2xl:min-w-[180px]">
            <option value="">{{$t('labels.period')}}</option>
            <option v-for="months in periods" :value="months">{{ months }} {{ months == 1 ? $t('placeholders.month') : $t('placeholders.months') }}</option>
          </select>
        </div>
      </div>
      <!-- Skeleton loader -->
      <div v-if="loading"
           class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-8">
        <CardPropertySkeleton v-for="n in hitsPerPage" :key="n"/>
      </div>
      <div v-else-if="hits && hits.length > 0"
           class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-8">
        <CardProperty :package-order="packageOrder" v-for="hit in hits" :key="hit.id" :listing="hit"/>
        <template v-if="loadingMore">
          <CardPropertySkeleton v-for="n in 12" :key="'more-' + n"/>
        </template>
      </div>
      <div v-if="hits && hits.length > 0 && !loading" class="mt-10 flex justify-center">
        <button
           @click="showMore"
           :disabled="loadingMore"
           class="w-full md:w-auto md:px-20 text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center disabled:opacity-50 disabled:cursor-not-allowed dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800">
          {{$t('buttons.more_properties')}} ({{ morePropertiesText }})
        </button>
      </div>
    </MicsContainer>
  </section>
</template>

<script setup lang="ts">
import { ref, toRaw } from 'vue';
import {useWebsiteConfigurationStore} from '~/stores/websiteConfigurationStore';
const websiteConfigurationStore = useWebsiteConfigurationStore();
const { $helpers } = useNuxtApp();
const {result, search} = useAlgoliaSearch('listings')
const hits = ref([]);
const packageOrder = ref('min_stay');
const totalHits = ref(0);
const loading = ref(true);
const loadingMore = ref(false);
const hitsPerPage = ref(12);

const locations = ref([]);
const allLocations = ref([]);
const activeLocation = ref(null);
const periods = [1, 2, 3, 4, 5, 6, 12];
const selectedPeriod = ref('');

const morePropertiesText = computed(() => {
  const remainingProperties = totalHits.value - hitsPerPage.value;
  if (remainingProperties > 100) {
    return '100+';
  }
  return Math.max(0, remainingProperties);
});

function locationCitySlugs(location) {
  // A region (e.g. pacific-coast-beaches) has no listings under its own slug;
  // its listings live under the child city slugs whose `region` points back to it.
  if (location.type === 'region') {
    return allLocations.value
      .filter(config => config.region === location.name)
      .map(config => config.name);
  }
  return [location.name];
}

function buildFilters() {
  let filters = `language_code:${websiteConfigurationStore.configuration.languageCode} AND listing_id:${websiteConfigurationStore.configuration.listingId} AND announce_type:property-announces AND ranking_score > 3000`;
  if (activeLocation.value) {
    const slugs = locationCitySlugs(activeLocation.value);
    if (slugs.length) {
      filters += ` AND (${slugs.map(slug => `location.city.slug:${slug}`).join(' OR ')})`;
    }
  }
  if (selectedPeriod.value) {
    filters += ` AND packages.min<=${selectedPeriod.value}`;
  }
  return filters;
}

function setLocationIndex(event) {
  setLocation(locations.value[event.target.value]);
}

function setLocation(location) {
  activeLocation.value = location;
  hitsPerPage.value = 12;
  runSearch();
}

function setPeriod() {
  hitsPerPage.value = 12;
  runSearch();
}

function showMore() {
  hitsPerPage.value += 12;
  runSearch(true);
}

async function runSearch(append = false) {
  if (append) {
    loadingMore.value = true;
  } else {
    loading.value = true;
  }
  await search({
    requestOptions: {
      hitsPerPage: hitsPerPage.value,
      filters: buildFilters()
    },
  });
  totalHits.value = result.value.nbHits;
  hits.value = toRaw(result.value.hits); // Assuming the result has a hits array
  loading.value = false;
  loadingMore.value = false;
}

onMounted(async () => {
  allLocations.value = $helpers.getConfigurationKey('locations');
  locations.value = allLocations.value.filter(location => location.primary);
  activeLocation.value = locations.value[0] ?? null;
  runSearch();
})
</script>